import { useMutation, useQuery } from '@tanstack/react-query';
import type { DataMetricDef, InitiativeMetricRef } from '@raro/domain';
import { Select } from '@raro/ui';
import { useState } from 'react';
import { api } from '../lib/api';

interface Source { id: string; name: string; metrics: DataMetricDef[] }

/**
 * Aferição pela série: liga a iniciativa a uma métrica dos dados do cliente. Na data marcada, o antes e o depois saem da série
 * sozinhos (ou agora, pelo botão). Some quando o cliente ainda não tem dados. Num VER localizado, vale o lugar do projeto.
 */
export function SeriesLink({ initiativeId, organizationId, metricRef, canEdit, onChange }: { initiativeId: string; organizationId: string; metricRef: InitiativeMetricRef | null | undefined; canEdit: boolean; onChange: () => void }) {
  const data = useQuery({ queryKey: ['data', organizationId], queryFn: () => api.get<{ enabled: boolean; sources: Source[] }>(`/organizations/${organizationId}/data`), staleTime: 60_000 });
  const [note, setNote] = useState<string | null>(null);
  const link = useMutation({ mutationFn: (value: string) => api.patch(`/initiatives/${initiativeId}`, { metricRef: value ? { dataSourceId: value.split('|')[0], metric: value.split('|')[1] } : null }), onSuccess: () => { setNote(null); onChange(); } });
  const measure = useMutation({
    mutationFn: () => api.post<{ measured: boolean; reason?: string }>(`/initiatives/${initiativeId}/measure-from-series`),
    onSuccess: (r) => { setNote(r.measured ? 'aferido pela série: o efeito está registrado.' : r.reason ?? 'não foi possível aferir'); if (r.measured) onChange(); },
    onError: (e) => setNote((e as Error).message),
  });
  const sources = data.data?.sources ?? [];
  if (sources.length === 0) return null;
  const current = metricRef ? sources.find((s) => s.id === metricRef.dataSourceId) : undefined;
  const metric = current?.metrics.find((m) => m.key === metricRef?.metric);
  return (
    <div className="rr-scout" style={{ marginTop: 6 }}>
      {metricRef ? (
        <>
          <span className="rr-faint">pela série: {metric?.label ?? metricRef.metric} · {current?.name ?? 'fonte removida'}{metricRef.pointId ? ' · só de um ponto' : metricRef.placeId ? ' · só de uma praça' : ' · cliente inteiro'}</span>
          {canEdit && <button type="button" className="rr-budget__toggle" onClick={() => measure.mutate()} disabled={measure.isPending}>{measure.isPending ? 'lendo a série…' : 'aferir pela série agora'}</button>}
          {canEdit && <button type="button" className="rr-budget__toggle" onClick={() => link.mutate('')} disabled={link.isPending}>desligar</button>}
        </>
      ) : canEdit ? (
        <Select value="" onChange={(e) => link.mutate(e.target.value)} aria-label="aferir pela série" style={{ width: 'auto', maxWidth: 340 }} disabled={link.isPending}>
          <option value="">aferir pela série de dados…</option>
          {sources.flatMap((s) => s.metrics.map((m) => <option key={`${s.id}|${m.key}`} value={`${s.id}|${m.key}`}>{m.label} · {s.name}</option>))}
        </Select>
      ) : null}
      {note && <span className={measure.data?.measured ? 'rr-faint' : 'rr-field__error'}>{note}</span>}
      {link.isError && <span className="rr-field__error">{(link.error as Error).message}</span>}
    </div>
  );
}
